import { Fragment, Slice, type Node as PMNode } from '@atlaskit/editor-prosemirror/model';
import { NodeSelection, type EditorState } from '@atlaskit/editor-prosemirror/state';

import type { AgentManagedExtensionAttributes } from '../../../common/agentManagedExtensionPluginType';
import { getNodeAiManagedObject, isAgentManagedExtension } from '../../../common/utils';

import { getLocalId } from './node-attrs';
import type { AgentManagedNodeInfo } from './types';

/**
 * Returns node.content for partial slices so identity is only preserved when the
 * whole agent-managed block was selected.
 */
export const unwrapPartiallySelectedNode = (slice: Slice, node: PMNode): PMNode | Fragment => {
	const attrs = node.attrs as AgentManagedExtensionAttributes;
	const localId = getLocalId(attrs);
	if (!localId) {
		return node;
	}

	const firstChildAttrs = slice.content.firstChild?.attrs as
		| AgentManagedExtensionAttributes
		| undefined;
	const lastChildAttrs = slice.content.lastChild?.attrs as
		| AgentManagedExtensionAttributes
		| undefined;
	const isFirstChild = getLocalId(firstChildAttrs) === localId;
	const isLastChild = getLocalId(lastChildAttrs) === localId;

	// Open slices mean ProseMirror only copied part of the node. Keeping identity on that
	// fragment could make a partial body look like a complete managed block.
	return (isFirstChild && slice.openStart > 0) || (isLastChild && slice.openEnd > 0)
		? node.content
		: node;
};

/** Returns complete managed block identities represented by a clipboard slice. */
export const getAgentManagedNodeInfosFromSlice = (slice: Slice): AgentManagedNodeInfo[] => {
	const found: AgentManagedNodeInfo[] = [];
	slice.content.descendants((node) => {
		if (!isAgentManagedExtension(node)) {
			return true;
		}

		const completeNode = unwrapPartiallySelectedNode(slice, node);
		if (completeNode !== node) {
			return false;
		}

		const attrs = node.attrs as AgentManagedExtensionAttributes;
		const localId = getLocalId(attrs);
		if (localId) {
			const aiManagedObjectId = getNodeAiManagedObject(attrs)?.id;
			found.push({
				...(aiManagedObjectId ? { aiManagedObjectId } : {}),
				localId,
			});
		}

		return false;
	});

	return found;
};

/**
 * Keyboard copy/cut of a selected bodied extension can serialize only the body slice.
 * When the node selection is the managed block itself, rebuild a closed whole-node slice.
 */
export const getSelectedAgentManagedNodeSlice = (state: EditorState): Slice | undefined => {
	const { selection } = state;
	if (!(selection instanceof NodeSelection) || !isAgentManagedExtension(selection.node)) {
		return undefined;
	}

	return new Slice(Fragment.from(selection.node), 0, 0);
};

const collectAgentManagedNodes = (state: EditorState): Map<string, AgentManagedNodeInfo> => {
	const found = new Map<string, AgentManagedNodeInfo>();
	state.doc.descendants((node) => {
		if (!isAgentManagedExtension(node)) {
			return true;
		}

		const attrs = node.attrs as AgentManagedExtensionAttributes;
		const localId = getLocalId(attrs);
		if (localId) {
			const aiManagedObjectId = getNodeAiManagedObject(attrs)?.id;
			found.set(localId, {
				...(aiManagedObjectId ? { aiManagedObjectId } : {}),
				localId,
			});
		}

		return false;
	});
	return found;
};

/** Compares localIds before and after a transaction batch to detect real inserts/removals. */
export const getAddedAndRemovedAgentManagedNodes = (
	oldState: EditorState,
	newState: EditorState,
): { added: AgentManagedNodeInfo[]; removed: AgentManagedNodeInfo[] } => {
	const oldNodes = collectAgentManagedNodes(oldState);
	const newNodes = collectAgentManagedNodes(newState);

	return {
		added: [...newNodes.values()].filter(({ localId }) => !oldNodes.has(localId)),
		removed: [...oldNodes.values()].filter(({ localId }) => !newNodes.has(localId)),
	};
};
